import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const localFaq = [
  {
    question: "Who offers interior design services near me?",
    answer: "The Design Proposal provides professional interior design planning services both locally and virtually. We serve homeowners across all 50 states with complete design proposals including layout plans, material selections, and renovation guidance. Local in-person services are available in select metropolitan areas. Contact us to check availability in your location.",
  },
  {
    question: "Can I get interior design services done remotely?",
    answer: "Yes, our entire process is designed to work seamlessly online. Clients nationwide receive complete, detailed design proposals without requiring in-person meetings. Professional interior design planning is fully accessible regardless of your location through our proven remote-first workflow and digital collaboration process.",
  },
  {
    question: "What is included in a local interior design plan?",
    answer: "Local design plans include full layout recommendations, finish and material selections, furniture direction, and a cohesive design strategy tailored to your home, style, and budget. Plans are structured to be contractor-ready and actionable — giving you everything needed to execute confidently.",
  },
  {
    question: "How do I start an interior design project near me?",
    answer: "You can begin by requesting a design consultation, where your goals, space dimensions, and style preferences are discussed before a structured proposal is created. The process works seamlessly both remotely and locally — simply reach out and we&apos;ll guide you through the next steps.",
  },
];

const cityAreas = [
  { city: "New York, NY", type: "Virtual + Local" },
  { city: "Los Angeles, CA", type: "Virtual + Local" },
  { city: "Chicago, IL", type: "Virtual" },
  { city: "Houston, TX", type: "Virtual" },
  { city: "Phoenix, AZ", type: "Virtual" },
  { city: "Philadelphia, PA", type: "Virtual" },
  { city: "San Antonio, TX", type: "Virtual" },
  { city: "San Diego, CA", type: "Virtual" },
  { city: "Dallas, TX", type: "Virtual" },
  { city: "Jacksonville, FL", type: "Virtual" },
  { city: "Austin, TX", type: "Virtual" },
  { city: "Nashville, TN", type: "Virtual" },
  { city: "Seattle, WA", type: "Virtual" },
  { city: "Denver, CO", type: "Virtual" },
  { city: "Atlanta, GA", type: "Virtual" },
  { city: "Miami, FL", type: "Virtual" },
];

export default function LocationPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main>
        <section className="pt-36 pb-24 relative min-h-[540px] flex items-center">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/33c2ff7ca04857e17048f0e5b051015b.jpg"
              alt="Interior design services near you nationwide"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/15"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-2xl">
              <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-4">
                Service Areas
              </span>
              <h1
                className="text-5xl lg:text-6xl font-semibold text-white leading-tight mb-5"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Interior Design Services Nationwide
              </h1>
              <p className="text-white/70 leading-relaxed mb-8 text-lg max-w-xl">
                The Design Proposal delivers professional interior design planning to homeowners across the country — virtually in all 50 states, locally in select areas.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/#proposal"
                  className="whitespace-nowrap inline-flex items-center gap-2 bg-[#C9A961] text-white font-medium px-7 py-4 rounded-md hover:bg-[#b8924d] transition-colors cursor-pointer"
                >
                  Get Your Design Proposal
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
                <a
                  href="tel:+18005550199"
                  className="whitespace-nowrap inline-flex items-center gap-2 border border-white/40 text-white font-medium px-7 py-4 rounded-md hover:bg-white/10 cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line"></i>
                  </div>
                  Call (800) 555-0199
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              <div>
                <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                  Our Coverage
                </span>
                <h2
                  className="text-4xl font-semibold text-[#111111] mb-6 leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Interior Design Services Near You
                </h2>
                <p className="text-[#5C5C5C] leading-relaxed mb-5">
                  The Design Proposal provides structured interior design plans for homeowners across the United States. Services include layout design, finish selection, and renovation planning — helping clients confidently complete projects with clear direction and reduced risk of costly mistakes.
                </p>
                <p className="text-[#5C5C5C] leading-relaxed mb-8">
                  Our remote-friendly process means you can receive a complete, professional design proposal regardless of where you&apos;re located. For select metropolitan areas, we also offer local in-person services.
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    {
                      icon: "ri-global-line",
                      title: "Virtual Design — All 50 States",
                      desc: "Remote interior design proposals delivered nationwide with full detail and professional quality.",
                    },
                    {
                      icon: "ri-map-pin-line",
                      title: "Local Projects Available",
                      desc: "In-person design services available in select areas. Contact us to check availability.",
                    },
                    {
                      icon: "ri-home-4-line",
                      title: "Residential Focus",
                      desc: "All services tailored specifically for homeowners — renovations, redesigns, and new builds.",
                    },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4 p-4 bg-[#FAFAFA] rounded-xl border border-[#EBEBEB]">
                      <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg border border-[#EBEBEB] shrink-0">
                        <i className={`${item.icon} text-[#C9A961]`}></i>
                      </div>
                      <div>
                        <div className="font-semibold text-[#111111] text-sm mb-1">{item.title}</div>
                        <p className="text-xs text-[#5C5C5C] leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="rounded-xl overflow-hidden border border-[#EBEBEB]" style={{ height: "460px" }}>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d24178688.87258853!2d-95.7129!3d37.0902!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    title="Service coverage map — United States interior design services"
                  ></iframe>
                </div>
                <p className="text-xs text-[#9B9390] mt-2 text-center">
                  Virtual interior design services available across all 50 states
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-[#111111]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                  About Our Reach
                </span>
                <h2
                  className="text-3xl font-semibold text-white leading-tight mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Nationwide Interior Design Planning
                </h2>
                <Link
                  to="/#proposal"
                  className="whitespace-nowrap inline-flex items-center gap-2 bg-[#C9A961] text-white text-sm font-medium px-5 py-2.5 rounded-md hover:bg-[#b8924d] transition-colors cursor-pointer mt-2"
                >
                  Get Your Proposal
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              </div>
              <div className="lg:col-span-8 space-y-4">
                <p className="text-white/60 leading-relaxed text-[15px]">
                  The Design Proposal provides interior design planning services to homeowners across the United States through a fully remote, digital-first workflow. Virtual interior design proposals include complete layout planning, material and finish selection, furniture direction, and renovation guidance — delivered with the same quality as local in-person services.
                </p>
                <p className="text-white/60 leading-relaxed text-[15px]">
                  Our process is designed to work seamlessly regardless of location. Clients in New York, Los Angeles, Chicago, Houston, Austin, Nashville, Denver, Seattle, Atlanta, and all major cities receive full design proposals without requiring onsite visits. For clients in select metropolitan areas, local in-person design services are also available.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                Active Areas
              </span>
              <h2
                className="text-3xl lg:text-4xl font-semibold text-[#111111]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Helping Homeowners Across the Country
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              {cityAreas.map((area) => (
                <div
                  key={area.city}
                  className="bg-white rounded-lg p-4 border border-[#EBEBEB] flex items-center justify-between hover:border-[#C9A961]/40 transition-colors"
                >
                  <div>
                    <div className="text-sm font-medium text-[#111111]">{area.city}</div>
                    <div className="text-xs text-[#5C5C5C] mt-0.5">{area.type}</div>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-map-pin-line text-[#C9A961] text-sm"></i>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-[#5C5C5C]">
              Don&apos;t see your city? We serve{" "}
              <strong className="text-[#111111]">all 50 states</strong> virtually.{" "}
              <Link to="/#proposal" className="text-[#C9A961] underline cursor-pointer">
                Request your proposal
              </Link>{" "}
              regardless of location.
            </p>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
              <div className="lg:col-span-2">
                <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                  Local SEO FAQ
                </span>
                <h2
                  className="text-3xl lg:text-4xl font-semibold text-[#111111] leading-tight mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Interior Design Near You — Questions Answered
                </h2>
                <p className="text-[#5C5C5C] text-sm leading-relaxed">
                  Common questions from homeowners searching for interior design services in their area.
                </p>
              </div>
              <div className="lg:col-span-3">
                {localFaq.map((item, idx) => (
                  <div key={idx} className="border-b border-[#F0EDE8]">
                    <button
                      className="w-full flex items-center justify-between py-5 text-left cursor-pointer group"
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    >
                      <span
                        className={`text-sm font-medium leading-snug pr-4 transition-colors ${
                          openFaq === idx ? "text-[#C9A961]" : "text-[#111111] group-hover:text-[#C9A961]"
                        }`}
                      >
                        {item.question}
                      </span>
                      <div
                        className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 transition-all ${
                          openFaq === idx ? "bg-[#C9A961]" : "border border-[#E8E3DC]"
                        }`}
                      >
                        <i
                          className={`${openFaq === idx ? "ri-subtract-line text-white" : "ri-add-line text-[#111111]"} text-sm`}
                        ></i>
                      </div>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openFaq === idx ? "max-h-48 pb-5" : "max-h-0"
                      }`}
                    >
                      <p className="text-[#5C5C5C] leading-relaxed text-sm">{item.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-[#111111]">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2
              className="text-3xl lg:text-4xl font-semibold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Get Your Design Plan Today
            </h2>
            <p className="text-white/55 mb-8 leading-relaxed">
              Professional interior design planning available nationwide — virtually or locally. Get started with a clear, actionable proposal tailored to your space.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#proposal"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-[#C9A961] text-white font-medium px-7 py-3.5 rounded-md hover:bg-[#b8924d] transition-colors cursor-pointer"
              >
                Get Your Design Proposal
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
              <a
                href="tel:+18005550199"
                className="whitespace-nowrap inline-flex items-center gap-2 border border-white/25 text-white font-medium px-7 py-3.5 rounded-md hover:border-[#C9A961] hover:text-[#C9A961] transition-all cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                Call Now
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
